import _defineProperty from "@babel/runtime/helpers/defineProperty";
/*
 *                     NOTICE
 *            COPYRIGHT 2018 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import { StringUtils } from "abweb/utils/StringUtils";
import { TypeUtils } from "abweb/utils/TypeUtils";
import * as React from "react";

/**
 * The basic properties for a React <div> element.
 */

/**
 * The basic properties for a React <input> element.
 */

/**
 * Utility to merge style coming from component props with local styling
 * @param propsStyle may be null or undefined
 * @param localStyle
 * @returns {CSSStyleDeclaration} Either localStyle,
 *  or a new object containing the properties from localStyle merged on top of properties from propsStyle
 */
export function mergePropsStyles(propsStyle, localStyle) {
  return propsStyle ? {
    ...propsStyle,
    ...localStyle
  } : localStyle;
}
export function mergePropsAttributes(propsAttributes, localAttributes) {
  return propsAttributes ? {
    ...propsAttributes,
    ...localAttributes
  } : localAttributes;
}
export class ReactUtils {
  /**
   * Construct similar to createRef in the newer version of react.
   * eg:
   * private textRefHolder = ReactUtils.createRefHolder<AbTextAreaWithSelection>();
   *
   * TODO - remove this when we upgrade react.
   * Warning - situations for using ref= should be pretty rare if following best react practices.
   * If you find yourself using lots of refs, run it by a few other people to get other ideas.
   *
   * Some situations where refs seem to be unavoidable:
   * third party libraries that require calling methods on react components.
   * Calling focus() on an element to programatically give it keyboard focus.
   * 
   * @deprecated in favor of React.createRef() and React.useRef().
   */
  static createRefHolder() {
    // start the holder object an any, so that it can be both a pure function and have a current property.
    const holder = e => {
      //console.log(`got ref`); //`
      holder.current = e;
    };

    // initialize the current value to null
    holder.current = null;
    return holder;
  }

  /**
   * Utility to prepare common props for use in the outermost div of a React component.
   * When rendering you can spread the result into the outermost element of a React component
   * NOTE className attribute is always added to the returned object.
   * This method does not populate the ReactDivProps 'tabIndex' prop (use ReactUtils::divProps for that).
   *
   * @returns an object containing className plus id, style, and data-test-id if they are part of the props arg, otherwise they are left out
   */
  static componentProps(componentClassName, props = {}, classNames = []) {
    const classes = ReactUtils.componentClassNamesString(componentClassName, props, classNames);
    let result = {
      className: classes
    };
    if (props.id != null) {
      result.id = props.id;
    }
    if (props.style != null) {
      result.style = props.style;
    }
    if (props.dataTestId != null) {
      result["data-test-id"] = props.dataTestId;
    }
    return result;
  }
  static divProps(componentClassName, props = {}, classNames = []) {
    const reactDivProps = ReactUtils.componentProps(componentClassName, props, classNames);
    if (props.tabIndex != null) {
      // Do NOT provide a default value if props.tabIndex not set, as no tabIndex means div is not tabbable
      reactDivProps.tabIndex = props.tabIndex;
    }
    return reactDivProps;
  }

  /**
  * extracts just the common props from a full set of props given to a component.
  * Useful if you need to pull out the common props to pass down as common props to the outermost rendered React component when there is no outermost DOM element
  * The result may have properties with the value 'undefined'. This should not matter for expected usage.
  * @param fullSetOfProps props to a component, where the props extend AbCommonProps
  * @result just the common props from fullSetOfProps
  */
  static extractCommonProps(fullSetOfProps) {
    const {
      id,
      style,
      dataTestId,
      className
    } = fullSetOfProps;
    // doesn't seem worth it to filter out properties with undefined values. More code, more time.
    return {
      id,
      style,
      dataTestId,
      className
    };
  }

  /**
   * extracts the non-common props from a full set of props given to a component.
   * Useful if you need to pull out the non-common props to avoid passing them down as common props to an inner element.
   * The result may have properties with the value 'undefined'. This should not matter for expected usage.
   * @param fullSetOfProps props to a component, where the props extend AbCommonProps
   * @result just the common props from fullSetOfProps
   */
  static extractNonCommonProps(fullSetOfProps) {
    const {
      id,
      style,
      dataTestId,
      className,
      ...rest
    } = fullSetOfProps;
    return rest;
  }

  /**
   * Utility to prepare common props for use in the outermost div of a React input component.
   * NOTE TabIndex is always added to props by this function.
   *
   * @returns an object containing all of the normal component props plus disabled and tabIndex
   */
  static inputProps(componentClassName, props = {}, classNames = []) {
    let result = {
      ...ReactUtils.componentProps(componentClassName, props, [...classNames, props.disabled && "ab-disabled"]),
      tabIndex: props.disabled ? -1 : props.tabIndex != null ? props.tabIndex : 0
    };
    if (props.name != null) {
      result.name = props.name;
    }
    if (props.disabled != null) {
      result.disabled = props.disabled;
    }
    if (props.disabled) {
      result["aria-disabled"] = "true";
    }
    if (props.autoFocus != null) {
      result.autoFocus = props.autoFocus;
    }
    return result;
  }

  /**
   * Returns a class name string for a React component.
   *
   * @param {string} componentClassName The class name for the component.
   * @param {AbCommonProps} props The common properties for the component.
   * @param {string[]} additionalClassNames An optional list of additional class names
   * @returns {string} A single string for use as a React component's className property
   */
  static componentClassNamesString(componentClassName, props, additionalClassNames = []) {
    const allClassNames = [componentClassName, props.className, ...additionalClassNames];
    return ReactUtils.classNamesString(allClassNames);
  }

  /**
   * Returns a single class name string from an array of class names.
   */
  static classNamesString(classNames) {
    // One might think using Array.join() would perform better.
    // Testing on chrome 69, Array.join() takes about twice as long for 8 strings as the concatenation below

    let result = "";
    for (let className of classNames) {
      if (className) {
        if (result === "") {
          result = className;
        } else {
          result += " " + className;
        }
      }
    }
    return result;
  }

  /**
   * Return HTML for the specified text with matches based upon the specified regular expression.
   *
   * @param text The text to be highlighted.
   * @param regExp The regular expression to match items in the text.
   */
  static renderTextWithHighlights(text, regExp) {
    return StringUtils.encodeHtml(text).replace(regExp, "<mark>$&</mark>");
  }

  /*
   * Deliberately removed, because it only worked for REACT 16:
   * findReactComponent(el: HTMLElement | null): React.ReactNode
   * Given a DOM element, find the nearest containing React component
   * @see https://stackoverflow.com/questions/29321742/react-getting-a-component-from-a-dom-element-for-debugging
   */

  static hasClassName(el, className) {
    return el.className.split(" ").includes(className);
  }

  /**
   * Utility for combining multiple refs to modify them all as if they were just one ref. The main use case is
   * when multiple refs need to all be put on a child component.
   *
   * Usage:
   * ...
   * const refFromParent = ....
   * const refForAutoFocus = ....
   * const refForSomethingElse = ....
   * return <div ref={ReactUtils.mergeRefs(refFromParent, refForAutoFocus, refForSomethingElse)}>Example</div>
   * ...
   * @param refs list of refs to merge
   */
}
_defineProperty(ReactUtils, "mergeRefs", (...refs) => {
  return value => {
    refs.forEach(ref => {
      if (typeof ref === "function") {
        ref(value);
      } else {
        if (!ref) return;
        ref.current = value;
      }
    });
  };
});
_defineProperty(ReactUtils, "castRef", ref => {
  return value => {
    if (typeof ref === "function") {
      ref(value);
    } else {
      if (ref === null) {
        return;
      }
      ref.current = value;
    }
  };
});
/**
 * Try an array of queries, returning the first element that matches. 
 */
_defineProperty(ReactUtils, "tryQueries", (root, ...queries) => {
  const filteredQueries = TypeUtils.filterOutNullishOrFalse(queries);
  for (const query of filteredQueries) {
    const result = root.querySelector(query);
    if (result) return result;
  }
  return null;
});
_defineProperty(ReactUtils, "isDomTypeElement", element => {
  return /*#__PURE__*/React.isValidElement(element) && typeof element.type === "string";
});
//# sourceMappingURL=ReactUtils.js.map
